import makeStyles from "@mui/styles/makeStyles";
import { alpha } from "@mui/material/styles";
import { FONT_DISPLAY } from "../../../utils/designTokens";

// ---- Design tokens (shared with the Home page's editorial style) -----------
const PAPER = "#FFF9F0"; // warm cream canvas
const GREEN = "#2E7D32"; // brand green (logo)
const SERIF = FONT_DISPLAY;

// Subtle paper-grain texture (inline SVG noise)
const GRAIN = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E")`;

const grainOverlay = (opacity) => ({
  content: '""',
  position: "absolute",
  inset: 0,
  backgroundImage: GRAIN,
  opacity,
  pointerEvents: "none",
  zIndex: 0,
});

// Entrance motion uses `backwards` fill, never `both`: a transform left on the
// card after it lands would become the containing block for the fixed-position
// FlashMessage snackbar rendered inside it.
const RISE = "480ms cubic-bezier(0.34, 1.36, 0.64, 1) backwards";

const useStyles = makeStyles((theme) => {
  const INK = theme.palette.common.black;
  const RED = theme.palette.primary.main;
  const mdDown = theme.breakpoints.down("md");

  return {
    root: {
      position: "relative",
      minHeight: "100vh",
      backgroundColor: PAPER,
      overflowX: "hidden",
      "&::before": { ...grainOverlay(0.05), position: "fixed" },

      // ---- Inputs --------------------------------------------------------
      "& .MuiInputBase-input": {
        color: theme.palette.text.secondary,
      },
      // Keep Chrome's blue autofill wash off the field.
      "& input:-webkit-autofill": {
        "-webkit-text-fill-color": theme.palette.text.secondary,
        transition: "background-color 600000s 0s, color 600000s 0s",
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: 14,
        backgroundColor: theme.palette.grey[50],
        transition: "background-color 200ms ease, box-shadow 200ms ease",
        "& fieldset": {
          borderColor: theme.palette.grey[300],
          borderWidth: 1.5,
          transition: "border-color 200ms ease",
        },
        "&:hover fieldset": {
          borderColor: theme.palette.grey[500],
        },
        "&.Mui-focused": {
          backgroundColor: theme.palette.common.white,
          boxShadow: `0 0 0 4px ${theme.palette.primary.lightest}`,
        },
        "&.Mui-focused fieldset": {
          borderColor: RED,
          borderWidth: 2,
        },
        "&.Mui-error fieldset": {
          borderColor: theme.palette.error.main,
        },
        "&.Mui-error.Mui-focused": {
          boxShadow: `0 0 0 4px ${theme.palette.error.lightest}`,
        },
        "& .MuiInputAdornment-positionStart svg": {
          color: theme.palette.grey[500],
          transition: "color 200ms ease",
        },
        "&.Mui-focused .MuiInputAdornment-positionStart svg": {
          color: RED,
        },
      },
      "& .MuiFormHelperText-root": {
        marginLeft: 2,
        fontWeight: 600,
      },

      "@media (prefers-reduced-motion: reduce)": {
        "& *, & *::before, & *::after": {
          animationDuration: "0.01ms !important",
          animationIterationCount: "1 !important",
          transitionDuration: "0.01ms !important",
        },
      },
    },

    main: {
      position: "relative",
      zIndex: 1,
      minHeight: "100vh",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "112px 24px 56px",
      [theme.breakpoints.down("sm")]: {
        alignItems: "flex-start",
        padding: "84px 16px 32px",
      },
    },

    // ---- Card: sticker-style, hard offset shadow like the Home cards -------
    card: {
      width: "100%",
      maxWidth: 1000,
      display: "grid",
      gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 1fr)",
      backgroundColor: theme.palette.common.white,
      border: `2px solid ${INK}`,
      borderRadius: 28,
      boxShadow: `10px 10px 0 ${INK}`,
      overflow: "hidden",
      animation: `$cardIn 560ms cubic-bezier(0.2, 0.8, 0.2, 1) backwards`,
      [mdDown]: {
        gridTemplateColumns: "minmax(0, 1fr)",
        maxWidth: 480,
        borderRadius: 24,
        boxShadow: `6px 6px 0 ${INK}`,
      },
    },
    "@keyframes cardIn": {
      from: { opacity: 0, transform: "translateY(28px) rotate(-0.6deg)" },
      to: { opacity: 1, transform: "translateY(0) rotate(0deg)" },
    },

    // ---- Brand pane --------------------------------------------------------
    brand: {
      position: "relative",
      isolation: "isolate",
      overflow: "hidden",
      minHeight: 520,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      padding: "40px 44px 44px",
      backgroundColor: INK,
      "&::before": grainOverlay(0.1),
      // Warm glow behind the plate
      "&::after": {
        content: '""',
        position: "absolute",
        top: "40%",
        left: "50%",
        width: 460,
        height: 460,
        marginTop: -230,
        marginLeft: -230,
        borderRadius: "50%",
        background: `radial-gradient(circle, ${alpha(RED, 0.24)} 0%, ${alpha(RED, 0)} 65%)`,
        pointerEvents: "none",
        zIndex: 0,
      },
      [mdDown]: {
        minHeight: 0,
        flexDirection: "row",
        alignItems: "center",
        gap: 16,
        padding: "28px 24px",
        "&::after": {
          top: "50%",
          left: "auto",
          right: -120,
          width: 300,
          height: 300,
          marginTop: -150,
          marginLeft: 0,
        },
      },
    },

    stage: {
      position: "relative",
      zIndex: 1,
      flex: 1,
      minHeight: 300,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      [mdDown]: {
        order: 2,
        flex: "0 0 auto",
        width: 104,
        height: 104,
        minHeight: 0,
      },
    },
    // Rings are centred with negative margins so `transform` stays free for
    // the spin / pulse animations.
    ring: {
      position: "absolute",
      top: "50%",
      left: "50%",
      borderRadius: "50%",
      pointerEvents: "none",
    },
    ringOrbit: {
      width: 330,
      height: 330,
      margin: -165,
      border: `1.5px solid ${alpha(GREEN, 0.75)}`,
      animation: "$spin 18s linear infinite reverse",
      "&::after": {
        content: '""',
        position: "absolute",
        top: -6,
        left: "50%",
        width: 12,
        height: 12,
        marginLeft: -6,
        borderRadius: "50%",
        backgroundColor: GREEN,
        boxShadow: `0 0 0 4px ${alpha(GREEN, 0.25)}`,
      },
      [mdDown]: { display: "none" },
    },
    ringDashed: {
      width: 240,
      height: 240,
      margin: -120,
      border: `2px dashed ${alpha(RED, 0.85)}`,
      animation: "$spin 32s linear infinite",
      [mdDown]: {
        width: 104,
        height: 104,
        margin: -52,
      },
    },
    ringPulse: {
      width: 172,
      height: 172,
      margin: -86,
      border: `2px solid ${RED}`,
      animation: "$pulse 2.8s cubic-bezier(0.2, 0.6, 0.3, 1) infinite",
      [mdDown]: {
        width: 84,
        height: 84,
        margin: -42,
      },
    },
    "@keyframes spin": {
      from: { transform: "rotate(0deg)" },
      to: { transform: "rotate(360deg)" },
    },
    "@keyframes pulse": {
      "0%": { transform: "scale(1)", opacity: 0.7 },
      "100%": { transform: "scale(1.65)", opacity: 0 },
    },

    plate: {
      position: "relative",
      zIndex: 1,
      width: 172,
      height: 172,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.common.white,
      boxShadow: `inset 0 0 0 8px ${theme.palette.grey[100]}, 0 24px 50px ${alpha("#000", 0.45)}`,
      animation: "$bob 5s ease-in-out infinite",
      "& img": {
        width: 104,
        height: 104,
        objectFit: "contain",
      },
      [mdDown]: {
        width: 84,
        height: 84,
        boxShadow: `inset 0 0 0 4px ${theme.palette.grey[100]}, 0 12px 26px ${alpha("#000", 0.45)}`,
        "& img": { width: 52, height: 52 },
      },
    },
    "@keyframes bob": {
      "0%, 100%": { transform: "translateY(0)" },
      "50%": { transform: "translateY(-8px)" },
    },

    copy: {
      position: "relative",
      zIndex: 1,
      animation: `$rise ${RISE}`,
      animationDelay: "180ms",
      [mdDown]: {
        flex: 1,
        minWidth: 0,
      },
    },
    heading: {
      margin: 0,
      fontFamily: SERIF,
      fontWeight: 700,
      fontSize: "clamp(2.5rem, 4.4vw, 3.5rem)",
      lineHeight: 1.02,
      letterSpacing: "-0.02em",
      color: theme.palette.common.white,
      [mdDown]: {
        fontSize: "2rem",
      },
    },
    subheading: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginTop: 14,
      fontSize: "1rem",
      fontWeight: 700,
      color: RED,
      "&::before": {
        content: '""',
        flex: "0 0 auto",
        width: 28,
        height: 2,
        borderRadius: 2,
        backgroundColor: RED,
      },
      [mdDown]: {
        marginTop: 8,
        fontSize: "0.875rem",
      },
    },

    // ---- Form pane ---------------------------------------------------------
    // Direct children and form rows rise in one after another. The snackbar
    // is skipped: it centres itself with translateX(-50%), which the rise
    // transform would override.
    pane: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      padding: "48px 48px 40px",
      "& > :not(.MuiSnackbar-root), & form > *": {
        animation: `$rise ${RISE}`,
      },
      "& > *:nth-child(2)": { animationDelay: "80ms" },
      "& > *:nth-child(3)": { animationDelay: "160ms" },
      "& > *:nth-child(4)": { animationDelay: "440ms" },
      "& > *:nth-child(5)": { animationDelay: "520ms" },
      "& > *:nth-child(6)": { animationDelay: "600ms" },
      "& form > *:nth-child(1)": { animationDelay: "200ms" },
      "& form > *:nth-child(2)": { animationDelay: "280ms" },
      "& form > *:nth-child(3)": { animationDelay: "360ms" },
      [mdDown]: {
        padding: "28px 24px",
      },
    },
    "@keyframes rise": {
      from: { opacity: 0, transform: "translateY(14px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
  };
});

export default useStyles;
